/**
 * The eight tag buckets, and how a `tropes` row lands in one.
 *
 * WHY THIS FILE EXISTS
 *
 * The reader's filter surfaces used to hardcode their own option lists —
 * AdvancedFiltersModal carried POV_OPTIONS, REPRESENTATION_OPTIONS and
 * SETTING_WORLD_OPTIONS as literal arrays, none of which existed in the
 * database. So a reader could pick "Second Person" and match nothing, forever,
 * because no book has ever been tagged with a string the author portal cannot
 * produce.
 *
 * The vocabulary a reader filters by has to be the vocabulary authors and
 * readers actually tagged with. That lives in one place, `public.tropes`, and
 * a row's bucket is decided by its `category` column.
 *
 * `tag_type` IS NOT `category`. They are different columns on different
 * tables, and the difference survives even where the two look alike: a
 * Representation tag is stored on book_tags with tag_type 'representation'
 * while its tropes row carries category 'Representation'. Same word, different
 * case, and the case matters — the matcher and the merge sweep key on the
 * capitalised spelling, book_tags_tag_type_check permits only the lowercase
 * one. Do not "fix" one by copying the other.
 *
 * THIS DOCBLOCK USED TO SAY THE TAG WAS STORED AS 'trope', and explained the
 * book detail modal's empty Representation row that way. That was wrong about
 * this repo even when it was written: resolveTagWithBucket has always used
 * CATEGORY_TO_TAG_TYPE in src/lib/tropeUtils.js, which maps Representation to
 * 'representation'. The author portal was the side filing them as 'trope', so
 * the modal's row showed the reader's tags and missed the author's — not empty,
 * half full, which is harder to notice.
 *
 * Settled by the founder: Representation is one of the eight buckets, stored
 * as itself, filterable as itself. The author portal now writes
 * 'representation' too, and the rows written under the old value were moved by
 * 20260906060000_representation_is_its_own_bucket.sql.
 */

/**
 * category -> bucket.
 *
 * An ALLOWLIST, deliberately. A category nobody has thought of yet has no
 * bucket, so its rows are simply not offered as filter chips. That is
 * recoverable. Guessing a bucket for it would put a mood in the trope section
 * and quietly widen everyone's results.
 *
 * The five legacy Title Case values — Relationship Dynamics, Characters &
 * Situations, Plot Devices, Fantasy Elements, Tone & Heat — are rows that
 * predate the eight buckets. They LOOKED like a layer underneath `trope` and
 * were never one: nothing in either repo ever wrote a single one, the author
 * form's trope list is one flat array, and #143 deleted the rows. `category`
 * has one job, which is what kind of tag a name is, and there is no level
 * below the eight.
 *
 * They are listed here purely so any book tagged through them before #143
 * stays filterable, and they map to the bucket they always meant. That is
 * backwards compatibility for old rows. Do not add a sixth.
 *
 * This mirrors CATEGORY_GROUPS in supabase/functions/sweep-tropes/index.ts,
 * with one difference worth knowing: this map carries 'pov' and 'setting' and
 * the sweep's copy does not yet. The two answer different questions — this one
 * "which section does this chip belong in", the sweep's "may these two rows
 * merge" — so they are not required to be identical, but a category present in
 * neither is a category no part of the system can see.
 */
export const CATEGORY_TO_BUCKET = {
  trope:                     'trope',
  'Relationship Dynamics':   'trope',
  'Characters & Situations': 'trope',
  'Plot Devices':            'trope',
  'Fantasy Elements':        'trope',

  genre:                     'genre',

  tone:                      'tone',
  'Tone & Heat':             'tone',

  spice:                     'spice',
  trigger:                   'trigger',
  pov:                       'pov',
  setting:                   'setting',

  Representation:            'representation',
};

export function bucketOf(category) {
  if (!category) return null;
  return CATEGORY_TO_BUCKET[category] ?? null;
}

/**
 * The author form's preset lists, per bucket.
 *
 * A NAME CAN LIVE IN MORE THAN ONE BUCKET, AND THAT IS THE DESIGN.
 *
 * The eight buckets are umbrellas, not a filing system that gives every name
 * exactly one home. The author form already works this way and has all along:
 * `Dystopian` is in its GENRES list AND its SETTINGS list, and `Gothic` is in
 * GENRES AND TROPES. Somebody writing a dystopian setting and somebody writing
 * dystopian fiction are both right, and neither has to lose.
 *
 * This matters because `tropes.category` is ONE value on ONE row shared by
 * every book carrying that name, so it can only ever say one thing. Reading the
 * filter's sections from it alone would force each name into a single section
 * and quietly delete the other readings — "Small Town" would stop being a trope
 * the moment it became a setting.
 *
 * So bucket membership is a UNION: a name belongs to a bucket if its category
 * maps there, OR if it is on the author form's list for that bucket. That is
 * what makes the sections permissive rather than exclusive.
 *
 * COPIED, not imported: these lists live in reanlo-author's
 * src/pages/AddBook.jsx (GENRES, TROPES, MOODS, WARNINGS, POV_OPTS, SETTINGS,
 * REP_OPTS) and the two repos cannot import from each other. They were
 * extracted from that file programmatically rather than retyped. If the author
 * form's lists change, this copy goes stale silently — the repo has a mechanism
 * for exactly that (scripts/mirrored.json), and putting these under it is the
 * right follow-up.
 */
export const PRESET_BUCKETS = {
  genre: [
    'Contemporary Romance', 'Historical Romance', 'Romantasy',
    'Dark Romance', 'Paranormal Romance', 'Rom-Com', 'Epic / High Fantasy',
    'Dark Fantasy', 'Urban Fantasy', 'Cozy Fantasy', 'Science Fiction',
    'Dystopian', 'Sci-Fi Romance', 'Mystery', 'Thriller / Suspense',
    'Horror', 'Gothic', 'Literary Fiction', 'Contemporary Fiction',
    'Historical Fiction', 'Young Adult', 'Dark Academia',
  ],
  trope: [
    'Enemies to Lovers', 'Friends to Lovers', 'Slow Burn',
    'Forced Proximity', 'Second Chance', 'Fake Relationship',
    'Forbidden Love', 'Grumpy x Sunshine', 'Only One Bed',
    'Marriage of Convenience', 'Secret Identity', 'Revenge',
    'Redemption Arc', 'Love Triangle', 'Soulmates', 'Amnesia',
    'Time Travel Romance', 'Chosen One', 'Heist', 'Psychological',
    'Cozy Mystery', 'Haunted House', 'Gothic', 'Coming of Age',
    'Grief and Loss', 'Dual Timeline', 'Regency Era',
  ],
  tone: [
    'Dark', 'Light & Fluffy', 'Angsty', 'Humorous', 'Emotional',
    'Fast-Paced', 'Character-Driven', 'Plot-Driven', 'Atmospheric',
    'Witty Banter', 'Cozy', 'Gritty', 'Hopeful', 'Melancholic',
    'Suspenseful', 'Lyrical', 'Prose-heavy',
  ],
  trigger: [
    'Violence', 'Sexual Assault', 'Abuse (Physical)', 'Abuse (Emotional)',
    'Self-Harm', 'Suicide', 'Death of Parent', 'Death of Child',
    'Animal Death', 'Addiction', 'Mental Illness', 'Eating Disorders',
    'Graphic Gore', 'Kidnapping', 'Torture', 'Child Abuse',
  ],
  pov: [
    'First Person', 'Third Person Limited', 'Third Person Omniscient',
    'Multiple MCs / POVs', 'Female MC', 'Male MC', 'Epistolary',
  ],
  setting: [
    'Urban', 'Small Town', 'Rural / Countryside', 'Coastal / Beach',
    'Mountains', 'Desert', 'Island', 'College / University', 'Workplace',
    'High School', 'Hospital / Medical', 'Military', 'Royal Court',
    'Fictional World', 'Alternate Reality', 'Post-Apocalyptic', 'Dystopian',
    'Space / Sci-Fi Setting', 'Historical Setting', 'Contemporary Setting',
  ],
  representation: [
    'BIPOC MC', 'LGBTQ+ MC', 'Disabled MC', 'Neurodivergent MC',
    'Plus-Size MC', 'Mental Health Rep', 'Chronic Illness Rep',
    'Single Parent', 'Interracial Relationship', 'Trans MC',
    'Non-Binary MC', 'Asexual / Aromantic Rep', 'Immigrant / Refugee MC',
    'Working Class MC',
  ],
};

/** Every bucket a name belongs to: its category's bucket, plus any preset list it is on. */
export function bucketsFor(name, category) {
  const found = new Set();
  const fromCategory = bucketOf(category);
  if (fromCategory) found.add(fromCategory);

  const needle = name?.trim().toLowerCase();
  if (needle) {
    for (const [bucket, list] of Object.entries(PRESET_BUCKETS)) {
      if (list.some(preset => preset.toLowerCase() === needle)) found.add(bucket);
    }
  }
  return [...found];
}

/** The eight, in the order the filter sheet shows them. */
export const BUCKETS = ['genre', 'spice', 'trigger', 'trope', 'tone', 'pov', 'setting', 'representation'];

/**
 * The reader sees three spice lanes; the author portal records five levels.
 *
 * Locked by the founder, Sep 5 2026: "no spice" is none and closed door, "low
 * spice" is mild and moderate, "high spice" is spicy and scorching.
 *
 * The stored names are SPICE_NAMES in reanlo-author/src/hooks/useBooks.js:65 —
 * 'No Spice', 'Mild', 'Moderate', 'Spicy', 'Scorching'. Note that "none and
 * closed door" is ONE stored value, not two: level 0's form label reads "No
 * Spice / Closed Door" but the tag it writes is 'No Spice'.
 *
 * The 'No Spice / Closed Door' spelling is carried in the no-spice lane as
 * well. The reader's own tag cloud has been offering that exact string as a
 * pill (IKnowWhatIWant.jsx popularTags), and the matcher compares canonical
 * names literally, so any row created down that route is spelled the long way
 * and would otherwise be unreachable from this lane. Listing both costs
 * nothing — a lane is an OR — and covers whichever spelling the data holds.
 */
export const SPICE_LANES = [
  { key: 'any',  label: 'any',        tags: [] },
  { key: 'none', label: 'no spice',   tags: ['No Spice', 'No Spice / Closed Door'] },
  { key: 'low',  label: 'low spice',  tags: ['Mild', 'Moderate'] },
  { key: 'high', label: 'high spice', tags: ['Spicy', 'Scorching'] },
];

export function spiceTagsForLane(key) {
  return SPICE_LANES.find(l => l.key === key)?.tags ?? [];
}
